import { Box, Group, Stack, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";

import {
  type AdminAuditEntry,
  type AuditDiffRow,
  auditTruncatedKilobytes,
  buildAuditDiffRows,
  isAuditSideTruncated,
} from "~/utils/adminAudit";

const FIELD_COLUMN_WIDTH = 160;

const ValueText = ({ value }: { value: string | null }) => (
  <Box style={{ flex: 1, minWidth: 0 }}>
    <Text
      size="xs"
      ff="monospace"
      c={value === null ? "var(--mantine-color-text-tertiary)" : undefined}
      style={{ overflowWrap: "anywhere" }}
    >
      {value ?? "—"}
    </Text>
  </Box>
);

const DiffRow = ({ row }: { row: AuditDiffRow }) => (
  <Group gap={8} wrap="nowrap" align="flex-start">
    <Box w={FIELD_COLUMN_WIDTH} style={{ flexShrink: 0 }}>
      <Text size="xs" fw={600} style={{ overflowWrap: "anywhere" }}>
        {row.field}
      </Text>
    </Box>
    <ValueText value={row.before} />
    <ValueText value={row.after} />
  </Group>
);

const AuditDetailPanel = ({ entry }: { entry: AdminAuditEntry }) => {
  const { t } = useTranslation("audit");

  const rows = buildAuditDiffRows(entry.before, entry.after);
  const beforeTruncatedKilobytes = isAuditSideTruncated(entry.before)
    ? auditTruncatedKilobytes(entry.before.bytes)
    : null;
  const afterTruncatedKilobytes = isAuditSideTruncated(entry.after)
    ? auditTruncatedKilobytes(entry.after.bytes)
    : null;
  const hasNothingToShow =
    rows.length === 0 &&
    beforeTruncatedKilobytes === null &&
    afterTruncatedKilobytes === null;

  return (
    <Stack gap={8} p={8}>
      <Group gap={8} wrap="nowrap" align="flex-start">
        <Box w={FIELD_COLUMN_WIDTH} style={{ flexShrink: 0 }}>
          <Text size="xs" fw={600}>
            {t("detail.requestId")}
          </Text>
        </Box>
        <Text size="xs" ff="monospace" style={{ overflowWrap: "anywhere" }}>
          {entry.requestId}
        </Text>
      </Group>
      <Group gap={8} wrap="nowrap" align="flex-start">
        <Box w={FIELD_COLUMN_WIDTH} style={{ flexShrink: 0 }}>
          <Text size="xs" fw={600} c="var(--mantine-color-text-tertiary)">
            {t("detail.field")}
          </Text>
        </Box>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Text size="xs" fw={600} c="var(--mantine-color-text-tertiary)">
            {t("detail.before")}
          </Text>
        </Box>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Text size="xs" fw={600} c="var(--mantine-color-text-tertiary)">
            {t("detail.after")}
          </Text>
        </Box>
      </Group>
      {rows.map((row) => (
        <DiffRow key={row.field} row={row} />
      ))}
      {beforeTruncatedKilobytes !== null && (
        <Text size="xs" c="var(--mantine-color-text-tertiary)">
          {t("detail.beforeTruncated", {
            kilobytes: beforeTruncatedKilobytes,
          })}
        </Text>
      )}
      {afterTruncatedKilobytes !== null && (
        <Text size="xs" c="var(--mantine-color-text-tertiary)">
          {t("detail.afterTruncated", { kilobytes: afterTruncatedKilobytes })}
        </Text>
      )}
      {hasNothingToShow && (
        <Text size="xs" c="var(--mantine-color-text-tertiary)">
          {t("detail.noChangedFields")}
        </Text>
      )}
    </Stack>
  );
};

export default AuditDetailPanel;
